import React, { useEffect, useId, useRef, useState } from "react";
import {
  registrationService,
  type SubscriptionPlan,
  type SubscriptionPrice,
} from "../services/registrationService";
import type { GrantsScheme } from "../hooks/useRegistrationAnalytics";
import { formatPrice } from "../utils/pricing";

/**
 * Last-resort card label when both `displayName` (catalog lookup gap, e.g.
 * seed not yet applied on this environment) and the legacy `name` are absent
 * — never render/announce the literal "undefined" to a paying customer.
 *
 * Exported because step 5 (`Step5Summary`) has to name the very same package
 * under the very same catalog gap: `/register/plans` sends `displayName` but
 * NO legacy `name` (registrationService.SubscriptionPlan), so the summary read
 * `selectedPlan.name` straight into the "Plan" row and rendered nothing at all.
 */
export const GRANTS_SCHEME_FALLBACK_LABELS: Record<GrantsScheme, string> = {
  kfs: "Pakiet KFS",
  bur: "Pakiet BUR",
  all: "Pakiet KFS+BUR",
};

/**
 * Grammatical adjective form for the "przy płatności ___" annotation. Falls
 * back to the raw adverb label for any interval this map doesn't know about.
 * Exported (LOW #5) so the anti-drift contract test can pin its keys against
 * the backend's getBillingIntervalLabel() without duplicating the component's
 * render logic.
 */
export const PAYMENT_ADJECTIVE_BY_INTERVAL_LABEL: Record<string, string> = {
  miesięcznie: "miesięcznej",
  kwartalnie: "kwartalnej",
  rocznie: "rocznej",
};

/** LOW #1: how long the just-selected package tile keeps its highlight ring. */
const HIGHLIGHT_DURATION_MS = 600;

/**
 * The package we point an undecided customer at. It is OUR recommendation
 * (the only package covering both funding sources plus the de minimis forms)
 * — deliberately not a "most popular" claim, which we have no data to back.
 */
const RECOMMENDED_SCHEME: GrantsScheme = "all";

/**
 * Monthly-equivalent amount (in cents) for a single price — quarterly/yearly
 * totals divided down to a per-month figure. Always the REGULAR amount, never
 * the promotional one: if the promotion ever returns, the "from" price on the
 * package tile must stay out of Omnibus territory (Technical Constraints).
 */
const getMonthlyEquivalentAmount = (price: SubscriptionPrice): number => {
  const totalMonths =
    price.interval === "year" ? price.intervalCount * 12 : price.intervalCount;
  return price.amount / totalMonths;
};

/**
 * AC-3: lowest monthly-equivalent amount (in cents) across a package's
 * prices. Pure and testable in isolation from JSX.
 */
export const getLowestMonthlyEquivalent = (prices: SubscriptionPrice[]): number => {
  return Math.min(...prices.map(getMonthlyEquivalentAmount));
};

/**
 * The price point every "taniej o X%" claim is measured against: the HIGHEST
 * monthly-equivalent in the package (in practice the pay-monthly option, the
 * "główna cena" a customer pays when they commit to nothing).
 *
 * Derived from the response rather than hardcoded to `billingInterval ===
 * 'monthly'` because `/register/plans` drops any interval that fails to price
 * (Promise.allSettled) — with the monthly option missing, the comparison must
 * silently fall back to the most expensive interval that IS on offer instead
 * of claiming a discount against a price nobody can buy.
 */
export const getReferenceMonthlyEquivalent = (prices: SubscriptionPrice[]): number => {
  return Math.max(...prices.map(getMonthlyEquivalentAmount));
};

export interface IntervalSavings {
  /**
   * Cents saved over 12 months versus paying at the reference rate. Annualized
   * on purpose: it makes quarterly and yearly directly comparable (a quarterly
   * saving of 20 PLN per quarter and a yearly one of 380 PLN are otherwise
   * measured on different scales).
   */
  amountPerYear: number;
  /** Whole-percent discount versus the reference rate. */
  percent: number;
}

/**
 * How much cheaper one interval is than paying at the reference (monthly)
 * rate — the numbers behind the "taniej o 17% / oszczędzasz 380 PLN rocznie"
 * copy on the interval cards.
 *
 * Returns `null` when there is nothing honest to claim: the reference price
 * itself, a single-price package, or a difference that rounds below 1%
 * (a "taniej o 0%" badge is noise, not persuasion).
 */
export const getIntervalSavings = (
  price: SubscriptionPrice,
  prices: SubscriptionPrice[]
): IntervalSavings | null => {
  if (prices.length === 0) return null;
  const reference = getReferenceMonthlyEquivalent(prices);
  const monthlyEquivalent = getMonthlyEquivalentAmount(price);
  if (!Number.isFinite(reference) || reference <= monthlyEquivalent) return null;

  const percent = Math.round(((reference - monthlyEquivalent) / reference) * 100);
  if (percent < 1) return null;

  return {
    amountPerYear: Math.round((reference - monthlyEquivalent) * 12),
    percent,
  };
};

interface RegistrationPlanSchemeSelectorProps {
  plans: SubscriptionPlan[];
  selectedScheme: GrantsScheme;
  selectedStripePriceId: string;
  onSchemeChange: (scheme: GrantsScheme) => void;
  onPriceChange: (stripePriceId: string) => void;
}

/** Numbered pill marking the two stages of the picker (package, then interval). */
const StageHeading: React.FC<{
  step: number;
  title: string;
  children: React.ReactNode;
}> = ({ step, title, children }) => (
  <div className="flex items-start gap-3">
    <span
      aria-hidden="true"
      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-700 text-xs font-bold text-white"
    >
      {step}
    </span>
    <div>
      <h3 className="text-base font-semibold text-gray-900">{title}</h3>
      <p className="text-sm text-gray-600 mt-0.5">{children}</p>
    </div>
  </div>
);

/**
 * Two-stage plan picker for registration step 4: a radiogroup of package
 * tiles (each showing a lowest-monthly "from" price) followed by the
 * interval grid of ONLY the currently selected package. Purely presentational
 * — all values are controlled via props, both radiogroups use native
 * `<input type="radio">` for keyboard semantics (AC-5).
 *
 * The two stages are visually separated (numbered headings + a panel around
 * the interval grid) because stacked look-alike tiles read as one long,
 * undifferentiated list of cards — the customer cannot tell which choice is
 * "what I monitor" and which is "how often I pay".
 */
export const RegistrationPlanSchemeSelector: React.FC<
  RegistrationPlanSchemeSelectorProps
> = ({ plans, selectedScheme, selectedStripePriceId, onSchemeChange, onPriceChange }) => {
  // MEDIUM #2: unique per component instance, so per-tile description ids
  // don't collide if this component is ever rendered more than once on the
  // same page (pattern already used by CatalogPlanSelector's labelId).
  const instanceId = useId();
  const isFirstRenderRef = useRef(true);
  const firstIntervalRadioRef = useRef<HTMLInputElement | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const [highlightedScheme, setHighlightedScheme] = useState<GrantsScheme | null>(null);

  // NO `?? plans[0]` fallback: `stripePriceId` (direct_monthly|quarterly|yearly)
  // is shared by every package, so substituting a different package here shows
  // ITS prices under the customer's chosen scope — while `grantsScheme` (the
  // only field the charge path reads) still says what they picked. Displayed
  // price and charged product then diverge silently. When the selected scope is
  // not on offer, `Register.tsx` reconciles `formData.grantsScheme` to a package
  // that IS (and says so in step 4); until then stage 2 simply stays hidden.
  const selectedPlan = plans.find(
    (p) => (p.grantsScheme ?? "all") === selectedScheme
  );

  // AC-5/AC-1: announce the reveal + move focus to the newly shown grid, but
  // only on a scheme CHANGE (a click) — not on the very first render, where
  // the scheme may already be resolved from a landing preselection and the
  // step heading (not this grid) is the correct focus target (AC-28).
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    const label =
      selectedPlan?.displayName ??
      selectedPlan?.name ??
      GRANTS_SCHEME_FALLBACK_LABELS[selectedScheme];
    setAnnouncement(`Wybrano ${label} — wybierz teraz okres rozliczeniowy poniżej`);
    setHighlightedScheme(selectedScheme);
    firstIntervalRadioRef.current?.focus();
    const timer = setTimeout(() => setHighlightedScheme(null), HIGHLIGHT_DURATION_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedScheme]);

  const selectedPlanScheme: GrantsScheme = selectedPlan?.grantsScheme ?? "all";
  const selectedPlanLabel = selectedPlan
    ? (selectedPlan.displayName ??
      selectedPlan.name ??
      GRANTS_SCHEME_FALLBACK_LABELS[selectedPlanScheme])
    : null;

  // "Najtaniej" marks the interval with the lowest monthly-equivalent — only
  // meaningful when there is something to be cheaper THAN.
  const cheapestPriceId =
    selectedPlan && selectedPlan.prices.length > 1
      ? selectedPlan.prices.reduce((cheapest, price) =>
          getMonthlyEquivalentAmount(price) < getMonthlyEquivalentAmount(cheapest)
            ? price
            : cheapest
        ).stripePriceId
      : null;

  return (
    <div className="space-y-6">
      {/* AC-5: screen-reader announcement for the interval grid reveal */}
      <p aria-live="polite" className="sr-only">
        {announcement}
      </p>

      {/* ── Stage 1: what the customer monitors ───────────────────────── */}
      <div className="space-y-3" data-testid="plan-stage-package">
        {/* Deliberately does NOT repeat the per-tile "zakres rozszerzysz
            później" promise — that reassurance is package-specific and lives
            on the narrow-scope tiles. Here the job is orientation: telling the
            customer which of the two decisions they are making right now. */}
        <StageHeading step={1} title="Wybierz pakiet">
          Decydujesz tylko o zakresie monitoringu — cenę wybierzesz w kroku 2.
        </StageHeading>

        <div className="space-y-3" role="radiogroup" aria-label="Wybierz pakiet">
          {plans.map((plan) => {
            const planScheme: GrantsScheme = plan.grantsScheme ?? "all";
            const planLabel =
              plan.displayName ?? plan.name ?? GRANTS_SCHEME_FALLBACK_LABELS[planScheme];
            const planSummary = plan.marketingDescription ?? plan.description;
            const isSelected = planScheme === selectedScheme;
            const isHighlighted = planScheme === highlightedScheme;

            const lowestPrice =
              plan.prices.length > 0
                ? plan.prices.reduce((lowest, price) =>
                    getMonthlyEquivalentAmount(price) < getMonthlyEquivalentAmount(lowest)
                      ? price
                      : lowest
                  )
                : null;
            // LOW #2: lowestPrice was already picked by scanning plan.prices once
            // (the reduce above) using the exact same comparator as
            // getLowestMonthlyEquivalent — re-deriving its own monthly-equivalent
            // amount avoids a second full-array scan for the same number.
            const lowestPriceDisplay = lowestPrice
              ? formatPrice(getMonthlyEquivalentAmount(lowestPrice), lowestPrice.currency, 2)
              : null;
            const lowestIntervalLabel = lowestPrice
              ? lowestPrice.billingIntervalLabel ||
                registrationService.getIntervalName(
                  lowestPrice.interval,
                  lowestPrice.intervalCount
                )
              : null;
            const paymentAdjective = lowestIntervalLabel
              ? PAYMENT_ADJECTIVE_BY_INTERVAL_LABEL[lowestIntervalLabel] ??
                lowestIntervalLabel
              : null;

            // MEDIUM #2: the visible planSummary and upgrade note carry
            // decision-relevant info that the radio's aria-label alone doesn't
            // (a long aria-label is worse than a separate description). The
            // note only exists for kfs/bur, so its id is included only then —
            // no dangling id reference for "all".
            const planSummaryId = planSummary
              ? `${instanceId}-summary-${planScheme}`
              : undefined;
            const upgradeNoteId =
              planScheme !== "all" ? `${instanceId}-note-${planScheme}` : undefined;
            const describedBy =
              [planSummaryId, upgradeNoteId].filter(Boolean).join(" ") || undefined;

            return (
              <label
                key={planScheme}
                data-testid={`plan-scheme-card-${planScheme}`}
                className={`block cursor-pointer border-2 rounded-lg p-4 transition ${
                  isSelected
                    ? "border-primary-500 bg-primary-50"
                    : "border-gray-200 hover:border-gray-300"
                } ${isHighlighted ? "ring-2 ring-primary-400" : ""}`}
              >
                <input
                  type="radio"
                  name="grantsScheme"
                  value={planScheme}
                  checked={isSelected}
                  onChange={() => onSchemeChange(planScheme)}
                  aria-label={
                    lowestPriceDisplay
                      ? `${planLabel} — od ${lowestPriceDisplay}/mc`
                      : `${planLabel} — cena niedostępna`
                  }
                  aria-describedby={describedBy}
                  className="sr-only"
                />
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex items-start gap-3">
                    {/* Visual radio dot — the real input is sr-only, and a tile
                        that looks identical to the interval cards below is
                        exactly what made the two stages indistinguishable. */}
                    <span
                      aria-hidden="true"
                      className={`mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                        isSelected
                          ? "border-primary-600 bg-primary-600"
                          : "border-gray-300 bg-white"
                      }`}
                    >
                      {isSelected && (
                        <span className="h-2 w-2 rounded-full bg-white" />
                      )}
                    </span>
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <h4 className="text-lg font-semibold text-gray-900">
                          {planLabel}
                        </h4>
                        {planScheme === RECOMMENDED_SCHEME && (
                          <span
                            data-testid={`scheme-recommended-badge-${planScheme}`}
                            className="rounded-full bg-primary-100 px-2 py-0.5 text-xs font-semibold text-primary-800"
                          >
                            Rekomendowany
                          </span>
                        )}
                      </div>
                      {planSummary && (
                        <p id={planSummaryId} className="text-sm text-gray-600 mt-1">
                          {planSummary}
                        </p>
                      )}
                      {planScheme !== "all" && (
                        <p
                          id={upgradeNoteId}
                          className="text-xs text-gray-500 mt-1"
                          data-testid={`scheme-upgrade-note-${planScheme}`}
                        >
                          Zakres można rozszerzyć później samodzielnie w panelu klienta.
                        </p>
                      )}
                    </div>
                  </div>

                  {lowestPriceDisplay ? (
                    <div className="shrink-0 sm:pl-4 sm:text-right">
                      <p className="whitespace-nowrap">
                        <span className="text-sm text-gray-500">od </span>
                        <span className="text-xl font-bold text-gray-900">
                          {lowestPriceDisplay}
                        </span>
                        <span className="text-sm text-gray-600">/mc</span>
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        przy płatności {paymentAdjective}
                      </p>
                      <p className="text-xs text-gray-500">
                        pozostałe okresy wybierzesz poniżej
                      </p>
                    </div>
                  ) : (
                    // LOW #3: /register/plans always returns 3 fully-priced
                    // packages today, so this is theoretical — but a silently
                    // empty price block reads as broken, not as "no price".
                    <p
                      className="text-sm text-gray-500 shrink-0 sm:pl-4 sm:text-right"
                      data-testid={`price-unavailable-${planScheme}`}
                    >
                      Cena niedostępna
                    </p>
                  )}
                </div>
              </label>
            );
          })}
        </div>
      </div>

      {/* ── Stage 2: how often the customer pays ──────────────────────── */}
      {/* Interval grid — exclusively the selected package's prices (AC-1) */}
      {selectedPlan && (
        <div
          className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4"
          data-testid="plan-stage-interval"
        >
          <StageHeading step={2} title="Wybierz okres rozliczeniowy">
            Ceny pakietu{" "}
            <span className="font-medium text-gray-900">{selectedPlanLabel}</span>. Im
            dłuższy okres, tym niższa cena za miesiąc — zakres pakietu jest ten sam.
          </StageHeading>

          <div
            className="grid grid-cols-1 md:grid-cols-3 gap-3"
            role="radiogroup"
            aria-label="Wybierz okres rozliczeniowy"
          >
            {selectedPlan.prices.map((price, index) => {
              const planScheme = selectedPlanScheme;
              const planLabel =
                selectedPlanLabel ?? GRANTS_SCHEME_FALLBACK_LABELS[planScheme];
              const isSelected = selectedStripePriceId === price.stripePriceId;
              const intervalLabel =
                price.billingIntervalLabel ||
                registrationService.getIntervalName(price.interval, price.intervalCount);

              // A promotion already renders its own before/after comparison —
              // stacking a second "taniej o X%" claim (computed from the
              // REGULAR amounts) on top of it would be two different discounts
              // pointing at the same card.
              const isPromoted = Boolean(price.promotion?.isActive);
              const savings = isPromoted
                ? null
                : getIntervalSavings(price, selectedPlan.prices);
              const isCheapest = !isPromoted && price.stripePriceId === cheapestPriceId;
              const comparisonId = `${instanceId}-comparison-${price.stripePriceId}`;

              return (
                <label
                  key={price.stripePriceId}
                  data-testid={`plan-card-${price.stripePriceId}-${planScheme}`}
                  className={`cursor-pointer border-2 rounded-lg p-4 bg-white transition ${
                    isSelected
                      ? "border-primary-500 bg-primary-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="stripePriceId"
                    value={price.stripePriceId}
                    checked={isSelected}
                    ref={index === 0 ? firstIntervalRadioRef : undefined}
                    onChange={() => onPriceChange(price.stripePriceId)}
                    aria-label={`${planLabel} — ${intervalLabel}`}
                    aria-describedby={isPromoted ? undefined : comparisonId}
                    className="sr-only"
                  />
                  <div className="text-center">
                    {/* Fixed-height badge row so the price line stays on the
                        same baseline across all three cards. */}
                    <div className="flex h-6 items-center justify-center">
                      {isCheapest && (
                        <span
                          data-testid={`interval-cheapest-badge-${price.stripePriceId}`}
                          className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-800"
                        >
                          Najtaniej
                        </span>
                      )}
                    </div>
                    {/* AC-15: Promotion support */}
                    {price.promotion?.isActive ? (
                      <>
                        {/* AC-L3 / D4: amber banner per plan (grandfathering, not discount) */}
                        <div
                          className="bg-amber-50 border border-amber-200 text-amber-800 text-xs font-medium rounded-md px-2 py-1 mb-2"
                          data-testid="price-increase-banner"
                        >
                          Do 30 czerwca:{" "}
                          {registrationService.formatPrice(
                            price.promotion.promotionalAmount,
                            price.currency
                          )}{" "}
                          zamiast{" "}
                          {registrationService.formatPrice(
                            price.promotion.originalAmount,
                            price.currency
                          )}{" "}
                          — oszczędź{" "}
                          {registrationService.formatPrice(
                            price.promotion.originalAmount -
                              price.promotion.promotionalAmount,
                            price.currency
                          )}
                        </div>
                        <div
                          className="text-sm text-gray-600 line-through"
                          aria-label={`Cena od 1 lipca 2026: ${registrationService.formatPrice(
                            price.promotion.originalAmount,
                            price.currency
                          )}`}
                        >
                          {registrationService.formatPrice(
                            price.promotion.originalAmount,
                            price.currency
                          )}
                        </div>
                        <div
                          className="text-2xl text-gray-900 font-bold"
                          aria-label="Aktualna cena"
                        >
                          {registrationService.formatPrice(
                            price.promotion.promotionalAmount,
                            price.currency
                          )}
                        </div>
                        <div className="text-sm text-amber-700 font-medium mt-1">
                          {price.promotion.label}
                        </div>
                        {price.promotion.lowestPrice30Days !== undefined && (
                          <dd
                            data-testid="lowest-30-day-step4"
                            className="text-xs text-gray-500 mt-1"
                          >
                            Najniższa cena z ostatnich 30 dni:{" "}
                            {registrationService.formatPrice(
                              price.promotion.lowestPrice30Days,
                              price.currency
                            )}{" "}
                            netto
                          </dd>
                        )}
                      </>
                    ) : (
                      <div className="text-2xl font-bold text-gray-900">
                        {registrationService.formatPrice(price.amount, price.currency)}
                      </div>
                    )}
                    {/* AC-14: net + VAT info */}
                    <div className="text-sm text-gray-600 mt-1">{intervalLabel}</div>

                    {/* The whole point of the interval grid: the same package
                        costs less per month the longer you commit. Without a
                        per-month figure the customer compares 190 / 550 / 1900
                        and reads the longest commitment as the most expensive. */}
                    {!isPromoted && (
                      <div
                        id={comparisonId}
                        className="mt-2 border-t border-gray-200 pt-2"
                      >
                        <p
                          data-testid={`interval-monthly-equivalent-${price.stripePriceId}`}
                          className="text-sm font-semibold text-gray-900"
                        >
                          {formatPrice(
                            getMonthlyEquivalentAmount(price),
                            price.currency,
                            2
                          )}
                          /mc
                        </p>
                        {savings ? (
                          <div
                            data-testid={`interval-savings-${price.stripePriceId}`}
                            className="mt-0.5 text-xs font-medium text-green-700"
                          >
                            <p>taniej o {savings.percent}%</p>
                            <p>
                              oszczędzasz{" "}
                              {formatPrice(savings.amountPerYear, price.currency)} rocznie
                            </p>
                          </div>
                        ) : (
                          <p
                            data-testid={`interval-reference-note-${price.stripePriceId}`}
                            className="mt-0.5 text-xs text-gray-500"
                          >
                            bez dłuższego zobowiązania
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
